import { useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  ChakraModalFooter,
  CheckIcon,
  Column,
  ExternalLinkIcon,
  Row,
  Text,
} from "@hightouch/htui";
import { NewFeatureDialog } from "src/components/new-feature-dialog";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import * as analytics from "src/lib/analytics";
import { Link, LinkButton, useLocation, useParams } from "src/router";

import bgImage from "src/assets/modals/marketing-agent-modal-bg.png";

import { useShouldShowAiFeaturesAnnouncement } from "src/hooks/use-should-show-ai-features-announcement";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { useAgentsOptOutEnabled } from "src/pages/agents/hooks/use-agents-opt-out-enabled";
import { useAgentsTier } from "src/pages/agents/hooks/use-agents-tier";
import { useShouldShowAgentsTos } from "src/pages/agents/hooks/use-should-show-agents-tos";

const AI_TERMS_URL = `${import.meta.env.VITE_WEBSITE_URL}/ai-addendum`;
const DOCS_URL = "https://docs.hightouch.com/agents/overview";
const AI_FEATURES_SETTINGS_PATH = "/organization/ai-features";

const CHECKLIST_ITEMS = [
  "Talk to your customer data directly",
  "Works across your marketing platforms and customer data",
  "Schedule recurring reports and share them with your team",
];

// Pages under nav sections where users may be tangentially interested in data
// agents: Activation, Customer Studio, and Intelligence.
const GLOBAL_AGENTS_TOS_ROOT_PATHS = new Set([
  "/home",
  "/audiences",
  "/journeys",
  "/profiles",
  "/priority-lists",
  "/performance",
  "/traits",
  "/schema-v2",
  "/governance",
  "/templates",
  "/syncs",
  "/models",
  "/sequences",
  "/analytics",
  "/dashboards",
  "/experiments",
  "/metrics",
  "/campaigns",
]);

type Surface = "agents" | "global";

export const AgentsTermsOfService = () => {
  const {
    setAgentsTosDismissed,
    setGlobalAgentsTosDismissed,
    shouldShowAgentsTos,
  } = useShouldShowAgentsTos();

  const dismiss = () => {
    setAgentsTosDismissed(true);
    setGlobalAgentsTosDismissed(true);
    analytics.track("Agents ToS Modal Dismissed", { surface: "agents" });
  };

  return (
    <AgentsTosDialog
      onClose={dismiss}
      onClickTryNow={dismiss}
      shouldShow={shouldShowAgentsTos}
      surface="agents"
    />
  );
};

export const ScopedAgentsTermsOfService = () => {
  const location = useLocation();
  const { root_workspace_slug: rootWorkspaceSlug } = useParams<{
    root_workspace_slug?: string;
  }>();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const isAgentsOptOutEnabled = useAgentsOptOutEnabled();
  const {
    setAgentsTosDismissed,
    setGlobalAgentsTosDismissed,
    shouldShowGlobalAgentsTos,
  } = useShouldShowAgentsTos();
  const { setIsDismissed: setAiFeaturesAnnouncementDismissed } =
    useShouldShowAiFeaturesAnnouncement();

  const workspaceRouteSegments = getWorkspaceRouteSegments(
    location.pathname,
    rootWorkspaceSlug,
  );
  const isInScope = isGlobalAgentsTosPath(workspaceRouteSegments);
  const { isPermitted: canViewAgents } = useResourcePermission(
    { v2: { resource: "workspace", grant: "can_view_agents" } },
    { enabled: isInScope && shouldShowGlobalAgentsTos && isAgentsEnabled },
  );
  const isExcludedAudienceDetailPath = isAudienceDetailPath(
    workspaceRouteSegments,
  );

  const dismissAnnouncementIfOptOutEnabled = () => {
    if (isAgentsOptOutEnabled) {
      setAiFeaturesAnnouncementDismissed(true);
    }
  };

  const dismiss = () => {
    setGlobalAgentsTosDismissed(true);
    dismissAnnouncementIfOptOutEnabled();
    analytics.track("Agents ToS Modal Dismissed", { surface: "global" });
  };

  const dismissAndOpenAgents = () => {
    setGlobalAgentsTosDismissed(true);
    setAgentsTosDismissed(true);
    dismissAnnouncementIfOptOutEnabled();
    analytics.track("Agents ToS Modal Dismissed", { surface: "global" });
  };

  return (
    <AgentsTosDialog
      onClose={dismiss}
      onClickTryNow={dismissAndOpenAgents}
      shouldShow={
        isInScope &&
        shouldShowGlobalAgentsTos &&
        isAgentsEnabled &&
        canViewAgents &&
        !isExcludedAudienceDetailPath
      }
      surface="global"
    />
  );
};

const AgentsTosDialog = ({
  onClose,
  onClickTryNow,
  shouldShow,
  surface,
}: {
  onClose: () => void;
  onClickTryNow: () => void;
  shouldShow: boolean;
  surface: Surface;
}) => {
  const tier = useAgentsTier();
  const isAgentsOptOutEnabled = useAgentsOptOutEnabled();
  const { isAdmin } = useShouldShowAiFeaturesAnnouncement();

  useEffect(() => {
    if (!shouldShow) return;
    analytics.track("Agents ToS Modal Shown", { surface });
  }, [shouldShow, surface]);

  if (!shouldShow) return null;

  return (
    <NewFeatureDialog
      onClose={onClose}
      media={{ type: "image", src: bgImage }}
      mediaPosition="top center"
    >
      <NewFeatureDialog.Body>
        <NewFeatureDialog.Heading>
          Introducing your marketing intelligence agent
        </NewFeatureDialog.Heading>
        <Text color="text.secondary">
          Get answers about your marketing performance, build audiences, and
          automate reports.
        </Text>
        <Column gap={2}>
          {CHECKLIST_ITEMS.map((item) => (
            <Row key={item} gap={2} align="flex-start">
              <Box
                as={CheckIcon}
                color="text.success"
                flexShrink={0}
                mt="2px"
              />
              <Text color="text.secondary">{item}</Text>
            </Row>
          ))}
        </Column>
        {tier === "free" && (
          <Box pr={2}>
            <Alert
              variant="inline"
              type="info"
              title="Included as a limited preview for your team"
              message="Your team shares a monthly pool of messages across the workspace. You won't be charged for overage, but continued access is not guaranteed."
            />
          </Box>
        )}
        <Text color="text.secondary">
          Use of this tool is subject to{" "}
          <Link href={AI_TERMS_URL} isExternal>
            Hightouch's AI terms
            <Box as={ExternalLinkIcon} ml={1} mb="1px" />
          </Link>
          {isAgentsOptOutEnabled && (
            <>
              . AI features are on by default for everyone in your organization.{" "}
              {isAdmin ? (
                <>
                  You can choose{" "}
                  <Link href={AI_FEATURES_SETTINGS_PATH} onClick={onClose}>
                    which ones
                  </Link>{" "}
                  are available at any time.
                </>
              ) : (
                "If you'd like to change which AI features are available, reach out to your org admin."
              )}
            </>
          )}
        </Text>
      </NewFeatureDialog.Body>
      <ChakraModalFooter mt={0} p={6}>
        <Row width="100%" justify="flex-end" align="center" gap={6}>
          <Link href={DOCS_URL} isExternal onClick={onClose}>
            View docs
            <Box as={ExternalLinkIcon} ml={1} mb="1px" />
          </Link>
          {surface === "global" ? (
            <LinkButton
              variant="primary"
              href="/agents"
              onClick={onClickTryNow}
            >
              Try now
            </LinkButton>
          ) : (
            <Button variant="primary" onClick={onClickTryNow}>
              Get started
            </Button>
          )}
        </Row>
      </ChakraModalFooter>
    </NewFeatureDialog>
  );
};

const isGlobalAgentsTosPath = (workspaceRouteSegments: string[]) => {
  const rootPath = getWorkspaceRouteRootPath(workspaceRouteSegments);

  return GLOBAL_AGENTS_TOS_ROOT_PATHS.has(rootPath);
};

const isAudienceDetailPath = (segments: string[]) => {
  return (
    segments[0] === "audiences" &&
    Boolean(segments[1]) &&
    segments[1] !== "new" &&
    segments[1] !== "admin-vqf-editor" &&
    segments[2] !== "clone"
  );
};

const getWorkspaceRouteRootPath = (segments: string[]) => {
  return segments[0] ? `/${segments[0]}` : "";
};

const getWorkspaceRouteSegments = (
  pathname: string,
  rootWorkspaceSlug: string | undefined,
) => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments[0] && rootWorkspaceSlug) {
    const firstSegment = decodeURIComponent(segments[0]);
    if (firstSegment === rootWorkspaceSlug) {
      return segments.slice(1);
    }
  }

  return segments;
};
